import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import {
  Card,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Icon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  TextField
} from "@mui/material";
import { get, post } from "utils/WebService";
import { useMaterialUIController } from "context";
import { MySwal } from "App";
import MDAuditModal from "components/MDAuditModal";

function RedistribuicaoCarteira() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Estados dos usuários e equipes
  const [usuarios, setUsuarios] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [apenasInativos, setApenasInativos] = useState(true);

  // Múltiplos Usuários Origem
  const [selectedUsuariosOrigem, setSelectedUsuariosOrigem] = useState([]);
  const [resumoOrigem, setResumoOrigem] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);

  // Filtro por Cliente(s)
  const [selectedClientes, setSelectedClientes] = useState([]);

  // Modal de Casos na Fila
  const [openCasosModal, setOpenCasosModal] = useState(false);
  const [casosList, setCasosList] = useState([]);
  const [loadingCasos, setLoadingCasos] = useState(false);
  const [filtroBuscaCaso, setFiltroBuscaCaso] = useState("");

  // Filtro de equipe destino
  const [selectedEquipeFiltro, setSelectedEquipeFiltro] = useState("");
  const [filtroBuscaOperadorDestino, setFiltroBuscaOperadorDestino] = useState("");

  // Filtro de quantidade limite
  const [quantidadeLimite, setQuantidadeLimite] = useState("");

  // Modo de distribuição
  const [modo, setModo] = useState("UNICO"); // "UNICO" ou "EQUITATIVO"
  const [selectedDestinoUnico, setSelectedDestinoUnico] = useState("");
  const [selectedDestinosMultiplos, setSelectedDestinosMultiplos] = useState([]);

  // Processamento
  const [loadingExecucao, setLoadingExecucao] = useState(false);

  // Auditoria
  const [openAuditModal, setOpenAuditModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const handleOpenAudit = () => {
    setOpenAuditModal(true);
    setLoadingAudit(true);
    get("/redistribuicao-carteira/historico-geral")
      .then((res) => {
        if (res && Array.isArray(res)) {
          setAuditLogs(res);
        } else {
          setAuditLogs([]);
        }
      })
      .catch(() => setAuditLogs([]))
      .finally(() => setLoadingAudit(false));
  };

  useEffect(() => {
    carregarUsuarios();
    carregarEquipes();
  }, []);

  const carregarUsuarios = () => {
    get("/redistribuicao-carteira/usuarios")
      .then((res) => {
        if (res && Array.isArray(res)) {
          setUsuarios(res);
        }
      })
      .catch((err) => console.error("Erro ao carregar usuários:", err));
  };

  const carregarEquipes = () => {
    get("/redistribuicao-carteira/equipes")
      .then((res) => {
        if (res && Array.isArray(res)) {
          setEquipes(res);
        }
      })
      .catch((err) => console.error("Erro ao carregar equipes:", err));
  };

  const handleUsuariosOrigemChange = (newValues) => {
    const newIds = newValues ? newValues.map((u) => u.idEncrypted) : [];
    setSelectedUsuariosOrigem(newIds);
    setResumoOrigem(null);
    setSelectedClientes([]);
    setSelectedDestinosMultiplos([]);
    setSelectedDestinoUnico("");
    setFiltroBuscaOperadorDestino("");

    if (newValues && newValues.length > 0) {
      const firstUser = newValues[0];
      if (firstUser.equipeIds && firstUser.equipeIds.length > 0) {
        setSelectedEquipeFiltro(String(firstUser.equipeIds[0]));
      } else {
        setSelectedEquipeFiltro("");
      }
    } else {
      setSelectedEquipeFiltro("");
    }

    if (newIds.length > 0) {
      setLoadingResumo(true);
      const idsParam = newIds.join(",");
      get(`/redistribuicao-carteira/resumo/${idsParam}`)
        .then((res) => {
          setResumoOrigem(res);
        })
        .catch((err) => {
          console.error("Erro ao carregar resumo:", err);
          MySwal.fire("Erro", "Não foi possível carregar o resumo dos operadores selecionados.", "error");
        })
        .finally(() => setLoadingResumo(false));
    }
  };

  const handleAbrirCasosModal = () => {
    if (selectedUsuariosOrigem.length === 0) return;
    setOpenCasosModal(true);
    setLoadingCasos(true);
    setFiltroBuscaCaso("");

    const idsParam = selectedUsuariosOrigem.join(",");
    get(`/redistribuicao-carteira/casos/${idsParam}`)
      .then((res) => {
        if (res && Array.isArray(res)) {
          setCasosList(res);
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar casos:", err);
        MySwal.fire("Erro", "Não foi possível carregar a lista de casos.", "error");
      })
      .finally(() => setLoadingCasos(false));
  };

  const usuariosOrigemFiltrados = usuarios.filter((u) => (apenasInativos ? !u.isAtivo : u.isAtivo));

  const selectedUsuariosOrigemObjects = usuariosOrigemFiltrados.filter((u) =>
    selectedUsuariosOrigem.includes(u.idEncrypted)
  );

  // Filtragem dos operadores destino elegíveis
  const usuariosDestinoElegiveis = usuarios.filter((u) => {
    if (!u.isAtivo) return false;
    if (selectedUsuariosOrigem.includes(u.idEncrypted)) return false;

    // Filtro por busca de texto (Nome ou Email)
    if (filtroBuscaOperadorDestino) {
      const term = filtroBuscaOperadorDestino.toLowerCase();
      const matchNome = u.nome && u.nome.toLowerCase().includes(term);
      const matchEmail = u.email && u.email.toLowerCase().includes(term);
      if (!matchNome && !matchEmail) return false;
    }

    // Filtro por equipe específica selecionada no dropdown
    if (selectedEquipeFiltro) {
      const eqIdNum = parseInt(selectedEquipeFiltro, 10);
      if (!u.equipeIds || !u.equipeIds.includes(eqIdNum)) {
        return false;
      }
    }

    return true;
  });

  const handleToggleDestinoMultiplo = (idEnc) => {
    if (selectedDestinosMultiplos.includes(idEnc)) {
      setSelectedDestinosMultiplos(selectedDestinosMultiplos.filter((id) => id !== idEnc));
    } else {
      setSelectedDestinosMultiplos([...selectedDestinosMultiplos, idEnc]);
    }
  };

  const handleSelectAllDestinos = (checked) => {
    if (checked) {
      setSelectedDestinosMultiplos(usuariosDestinoElegiveis.map((u) => u.idEncrypted));
    } else {
      setSelectedDestinosMultiplos([]);
    }
  };

  const getFichasFiltradasPorClienteTotal = () => {
    if (!resumoOrigem || !resumoOrigem.clientesList) return resumoOrigem?.totalFichasAtivas || 0;
    if (selectedClientes.length === 0) return resumoOrigem.totalFichasAtivas;

    const targetPessoaIds = selectedClientes.map((c) => c.id);
    return resumoOrigem.clientesList
      .filter((c) => targetPessoaIds.includes(c.id))
      .reduce((sum, c) => sum + (c.quantidade || 0), 0);
  };

  const getQuantidadeDisponivel = () => {
    const baseTotal = getFichasFiltradasPorClienteTotal();
    if (!baseTotal) return 0;
    const qtdLimit = parseInt(quantidadeLimite, 10);
    if (!isNaN(qtdLimit) && qtdLimit > 0 && qtdLimit < baseTotal) {
      return qtdLimit;
    }
    return baseTotal;
  };

  const validDestinosMultiplos = selectedDestinosMultiplos.filter((idEnc) =>
    usuariosDestinoElegiveis.some((u) => u.idEncrypted === idEnc)
  );

  const getCalculoDestinoText = () => {
    const qtd = getQuantidadeDisponivel();
    if (modo === "UNICO") {
      const uDest = usuariosDestinoElegiveis.find((u) => u.idEncrypted === selectedDestinoUnico);
      if (!uDest) return "Selecione um operador de destino visível na lista.";
      return `Todas as ${qtd} fichas serão transferidas para o operador: ${uDest.nome}.`;
    } else {
      const numDest = validDestinosMultiplos.length;
      if (numDest === 0) return "Selecione ao menos um operador de destino visível na lista.";
      const porOperador = Math.floor(qtd / numDest);
      const sobra = qtd % numDest;
      return `${qtd} fichas serão divididas entre ${numDest} operadores (~${porOperador} fichas por operador${sobra > 0 ? `, com ${sobra} de sobra distribuídas aos primeiros` : ""}).`;
    }
  };

  const handleExecutarRedistribuicao = () => {
    if (selectedUsuariosOrigem.length === 0) {
      MySwal.fire("Atenção", "Selecione ao menos um funcionário de origem.", "warning");
      return;
    }

    let idsDestino = [];
    if (modo === "UNICO") {
      const uDest = usuariosDestinoElegiveis.find((u) => u.idEncrypted === selectedDestinoUnico);
      if (!selectedDestinoUnico || !uDest) {
        MySwal.fire("Atenção", "Selecione um operador de destino visível.", "warning");
        return;
      }
      idsDestino = [selectedDestinoUnico];
    } else {
      if (validDestinosMultiplos.length === 0) {
        MySwal.fire("Atenção", "Selecione ao menos um operador de destino visível para a divisão.", "warning");
        return;
      }
      idsDestino = validDestinosMultiplos;
    }

    const qtdTotal = getQuantidadeDisponivel();
    const nomesOrigem = selectedUsuariosOrigemObjects.map((u) => u.nome).join(", ");
    const clientesFiltroTexto = selectedClientes.length > 0
      ? `Filtrado para os clientes: <strong>${selectedClientes.map((c) => c.nome).join(", ")}</strong>.<br/>`
      : "";

    MySwal.fire({
      title: "Confirmar Redistribuição?",
      html: `Você está prestes a redistribuir <strong>${qtdTotal} fichas</strong> do(s) operador(es) <strong>${nomesOrigem}</strong>.<br/>${clientesFiltroTexto}<br/><small>${getCalculoDestinoText()}</small>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, Redistribuir Agora",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2e7d32"
    }).then((result) => {
      if (result.isConfirmed) {
        setLoadingExecucao(true);
        const payload = {
          idsUsuariosOrigem: selectedUsuariosOrigem,
          idsUsuariosDestino: idsDestino,
          idsPessoas: selectedClientes.map((c) => c.id),
          quantidade: quantidadeLimite ? parseInt(quantidadeLimite, 10) : null,
          modo: modo
        };

        post("/redistribuicao-carteira/executar", payload)
          .then((res) => {
            if (res && res.sucesso) {
              MySwal.fire(
                "Redistribuição Concluída!",
                `Foram redistribuídas ${res.totalRedistribuido} fichas com sucesso.`,
                "success"
              );
              // Recarrega resumo
              handleUsuariosOrigemChange(selectedUsuariosOrigemObjects);
            } else {
              MySwal.fire("Erro", res?.message || "Ocorreu um erro ao redistribuir.", "error");
            }
          })
          .catch((err) => {
            console.error("Erro na redistribuição:", err);
            MySwal.fire("Erro", "Não foi possível concluir a redistribuição.", "error");
          })
          .finally(() => setLoadingExecucao(false));
      }
    });
  };

  const isAllDestinosSelected =
    usuariosDestinoElegiveis.length > 0 &&
    usuariosDestinoElegiveis.every((u) => selectedDestinosMultiplos.includes(u.idEncrypted));

  const getTextFieldStyle = (darkMode) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
      borderRadius: "8px",
      p: "4px 8px !important",
      "& fieldset": {
        borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "#d2d6da"
      },
      "&:hover fieldset": {
        borderColor: darkMode ? "#00b0ff" : "#1A73E8"
      },
      "&.Mui-focused fieldset": {
        borderColor: darkMode ? "#00b0ff" : "#1A73E8"
      }
    },
    "& .MuiInputBase-input": {
      color: darkMode ? "#ffffff !important" : "#000000 !important",
      fontSize: "0.875rem"
    }
  });

  const selectedClienteIds = selectedClientes.map((c) => c.id);

  const casosFiltrados = casosList.filter((c) => {
    if (selectedClienteIds.length > 0 && (!c.idPessoaCliente || !selectedClienteIds.includes(c.idPessoaCliente))) {
      return false;
    }
    if (!filtroBuscaCaso) return true;
    const term = filtroBuscaCaso.toLowerCase();
    return (
      (c.nomeDevedor && c.nomeDevedor.toLowerCase().includes(term)) ||
      (c.documento && c.documento.toLowerCase().includes(term)) ||
      (c.cliente && c.cliente.toLowerCase().includes(term)) ||
      (c.carteira && c.carteira.toLowerCase().includes(term)) ||
      (c.idFicha && String(c.idFicha).includes(term))
    );
  });

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* CARD 1: SELEÇÃO E RESUMO DOS OPERADORES ORIGEM */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: darkMode ? "#4B4F5C" : "#fafafa",
              p: 3,
              borderRadius: 2
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8} display="flex" alignItems="center" pt={{ xs: 0, sm: 2.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={apenasInativos}
                      onChange={(e) => {
                        setApenasInativos(e.target.checked);
                        setSelectedUsuariosOrigem([]);
                        setResumoOrigem(null);
                        setSelectedClientes([]);
                      }}
                      color="info"
                    />
                  }
                  label={
                    <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                      {apenasInativos ? "Exibindo apenas Usuários Inativos" : "Exibindo apenas Usuários Ativos"}
                    </MDTypography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4} display="flex" justifyContent="flex-end" pt={{ xs: 0, sm: 2.5 }}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleOpenAudit}
                >
                  <Icon sx={{ mr: 0.5 }}>history</Icon>
                  Histórico de Redistribuições
                </MDButton>
              </Grid>

              <Grid item xs={12}>
                <MDBox>
                  <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={0.5}>
                    Selecionar Funcionário(s) Origem (Retirar Fichas)
                  </MDTypography>
                  <Autocomplete
                    multiple
                    options={usuariosOrigemFiltrados}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option.nome} (${option.email || "Sem e-mail"})${option.equipes?.length > 0 ? ` [${option.equipes.join(", ")}]` : ""}`
                    }
                    value={selectedUsuariosOrigemObjects}
                    onChange={(event, newValue) => {
                      handleUsuariosOrigemChange(newValue);
                    }}
                    isOptionEqualToValue={(option, value) => option.idEncrypted === value?.idEncrypted}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.idEncrypted}
                          label={`${option.nome}${option.equipes?.length > 0 ? ` [${option.equipes.join(", ")}]` : ""}`}
                          size="small"
                          color="info"
                          {...getTagProps({ index })}
                          sx={{ m: 0.3, my: 0.3 }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        placeholder={selectedUsuariosOrigemObjects.length === 0 ? "Digite para pesquisar e selecionar 1 ou mais operadores de origem..." : ""}
                        fullWidth
                        sx={getTextFieldStyle(darkMode)}
                      />
                    )}
                    noOptionsText="Nenhum usuário encontrado"
                  />
                </MDBox>
              </Grid>
            </Grid>

            {loadingResumo && (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress color="info" />
              </MDBox>
            )}

            {resumoOrigem && !loadingResumo && (
              <MDBox mt={3}>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="stretch">
                  {/* KPI CARD 1 */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        background: darkMode
                          ? "linear-gradient(145deg, #606472 0%, #505461 100%)"
                          : "#ffffff",
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center",
                        textAlign: "center"
                      }}
                    >
                      <MDTypography variant="caption" color={darkMode ? "white" : "dark"} fontWeight="bold">
                        TOTAL DE FICHAS ATIVAS ({selectedUsuariosOrigemObjects.length} OPERADOR{selectedUsuariosOrigemObjects.length > 1 ? "ES" : ""})
                      </MDTypography>
                      <MDTypography variant="h2" sx={{ color: darkMode ? "#90caf9" : "#1976d2" }} fontWeight="bold" my={1}>
                        {getFichasFiltradasPorClienteTotal()}
                        {selectedClientes.length > 0 && (
                          <MDTypography variant="caption" color="text" display="block">
                            (de {resumoOrigem.totalFichasAtivas} totais)
                          </MDTypography>
                        )}
                      </MDTypography>
                      <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                          Operador(es): <strong>{resumoOrigem.nomeUsuarioOrigem}</strong>
                        </MDTypography>
                        <MDButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={handleAbrirCasosModal}
                          startIcon={<Icon>visibility</Icon>}
                        >
                          Ver Casos na Fila
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* KPI CARD 2: CARTEIRAS */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        background: darkMode
                          ? "linear-gradient(145deg, #606472 0%, #505461 100%)"
                          : "#ffffff",
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start"
                      }}
                    >
                      <MDTypography variant="caption" color={darkMode ? "white" : "dark"} fontWeight="bold" mb={1}>
                        FICHAS POR CARTEIRA
                      </MDTypography>
                      <MDBox maxHeight={140} sx={{ overflowY: "auto", flexGrow: 1 }}>
                        {Object.entries(resumoOrigem.fichasPorCarteira || {}).map(([cart, qtd]) => (
                          <MDBox key={cart} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>
                              {cart}
                            </MDTypography>
                            <Chip label={qtd} size="small" color="info" />
                          </MDBox>
                        ))}
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* KPI CARD 3: CLIENTES */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        background: darkMode
                          ? "linear-gradient(145deg, #606472 0%, #505461 100%)"
                          : "#ffffff",
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start"
                      }}
                    >
                      <MDTypography variant="caption" color={darkMode ? "white" : "dark"} fontWeight="bold" mb={1}>
                        FICHAS POR CLIENTE
                      </MDTypography>
                      <MDBox maxHeight={140} sx={{ overflowY: "auto", flexGrow: 1 }}>
                        {Object.entries(resumoOrigem.fichasPorCliente || {}).map(([cli, qtd]) => (
                          <MDBox key={cli} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>
                              {cli}
                            </MDTypography>
                            <Chip label={qtd} size="small" color="success" />
                          </MDBox>
                        ))}
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            )}
          </Card>
        </Grid>

        {/* CARD 2: CONFIGURAÇÃO DE REDISTRIBUIÇÃO DE DESTINO */}
        {resumoOrigem && (
          <Grid item xs={12}>
            <Card
              sx={{
                background: darkMode ? "#4B4F5C" : "#fafafa",
                p: 3,
                borderRadius: 2
              }}
            >
              <Grid container spacing={3}>
                {/* FILTRO DE CLIENTE(S) ESPECÍFICO(S) */}
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={0.5}>
                      Filtrar Redistribuição por Cliente(s) Específico(s)
                    </MDTypography>
                    <Autocomplete
                      multiple
                      options={resumoOrigem?.clientesList || []}
                      getOptionLabel={(option) => `${option.nomeFantasia || option.nome} (${option.quantidade} fichas)`}
                      value={selectedClientes}
                      onChange={(event, newValue) => setSelectedClientes(newValue || [])}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.id}
                            label={`${option.nomeFantasia || option.nome} (${option.quantidade})`}
                            size="small"
                            color="success"
                            {...getTagProps({ index })}
                            sx={{ m: 0.3, my: 0.3 }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder={selectedClientes.length === 0 ? "Selecione os clientes (Vazio = Todos os Clientes)..." : ""}
                          fullWidth
                          sx={getTextFieldStyle(darkMode)}
                        />
                      )}
                      noOptionsText="Nenhum cliente disponível"
                    />
                  </MDBox>
                  <MDTypography variant="caption" color="text" mt={0.5} display="block">
                    Total disponível após filtro por cliente: <strong>{getFichasFiltradasPorClienteTotal()}</strong> fichas.
                  </MDTypography>
                </Grid>

                {/* ESCOPO / LIMITE DE QUANTIDADE */}
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={0.5}>
                      Quantidade Limite a Redistribuir
                    </MDTypography>
                    <MDInput
                      type="number"
                      variant="standard"
                      placeholder="Vazio = Todas do Cliente/Origem..."
                      value={quantidadeLimite}
                      onChange={(e) => setQuantidadeLimite(e.target.value)}
                      fullWidth
                    />
                  </MDBox>
                  <MDTypography variant="caption" color="text" mt={0.5} display="block">
                    Fichas que serão efetivamente redistribuídas: <strong>{getQuantidadeDisponivel()}</strong>.
                  </MDTypography>
                </Grid>

                {/* MODO DE DISTRIBUIÇÃO */}
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={0.5}>
                      Modo de Distribuição
                    </MDTypography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={modo}
                        onChange={(e) => {
                          setModo(e.target.value);
                          setSelectedDestinoUnico("");
                          setSelectedDestinosMultiplos([]);
                        }}
                        sx={{ height: 45 }}
                      >
                        <MenuItem value="UNICO">Transferência Direta (1 Único Operador)</MenuItem>
                        <MenuItem value="EQUITATIVO">Divisão Igualitária (Entre Múltiplos Operadores)</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>
                </Grid>

                {/* FILTRO DE EQUIPE DESTINO */}
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={0.5}>
                      Filtrar Destinos por Equipes
                    </MDTypography>
                    <Autocomplete
                      options={equipes}
                      getOptionLabel={(option) => option.nome || ""}
                      value={equipes.find((eq) => String(eq.id) === String(selectedEquipeFiltro)) || null}
                      onChange={(event, newValue) => setSelectedEquipeFiltro(newValue ? String(newValue.id) : "")}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder="Selecione a equipe (Vazio = Todas as Equipes)..."
                          fullWidth
                          sx={getTextFieldStyle(darkMode)}
                        />
                      )}
                      noOptionsText="Nenhuma equipe encontrada"
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                {/* SELEÇÃO DESTINO: ÚNICO */}
                {modo === "UNICO" && (
                  <Grid item xs={12}>
                    <MDBox>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={0.5}>
                        Novo Operador Responsável (Destino)
                      </MDTypography>
                      <Autocomplete
                        options={usuariosDestinoElegiveis}
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : `${option.nome} (${option.email || "Sem e-mail"})${option.equipes?.length > 0 ? ` [${option.equipes.join(", ")}]` : ""}`
                        }
                        value={usuariosDestinoElegiveis.find((u) => u.idEncrypted === selectedDestinoUnico) || null}
                        onChange={(event, newValue) => setSelectedDestinoUnico(newValue ? newValue.idEncrypted : "")}
                        isOptionEqualToValue={(option, value) => option.idEncrypted === value?.idEncrypted}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder={!selectedDestinoUnico ? "Digite para pesquisar e selecionar o novo operador responsável..." : ""}
                            fullWidth
                            sx={getTextFieldStyle(darkMode)}
                          />
                        )}
                        noOptionsText="Nenhum operador elegível encontrado"
                      />
                    </MDBox>
                  </Grid>
                )}

                {/* SELEÇÃO DESTINO: MÚLTIPLOS (DIVISÃO EQUITATIVA) */}
                {modo === "EQUITATIVO" && (
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                        Selecione os Operadores Ativos que receberão a fração das fichas ({usuariosDestinoElegiveis.length} disponíveis):
                      </MDTypography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isAllDestinosSelected}
                            onChange={(e) => handleSelectAllDestinos(e.target.checked)}
                            color="info"
                          />
                        }
                        label={
                          <MDTypography variant="caption" color={darkMode ? "white" : "dark"} fontWeight="bold">
                            Selecionar Todos
                          </MDTypography>
                        }
                      />
                    </MDBox>

                    {/* CAMPO DE PESQUISA EM TEMPO REAL NA TABELA DESTINO */}
                    <MDBox mb={1.5}>
                      <MDInput
                        variant="standard"
                        placeholder="Pesquisar Operador Destino por Nome ou E-mail..."
                        value={filtroBuscaOperadorDestino}
                        onChange={(e) => setFiltroBuscaOperadorDestino(e.target.value)}
                        fullWidth
                      />
                    </MDBox>

                    <TableContainer
                      sx={{
                        background: darkMode ? "linear-gradient(145deg, #606472 0%, #505461 100%)" : "#ffffff",
                        borderRadius: 1.5,
                        maxHeight: 320,
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                      }}
                    >
                      <Table size="small" sx={{ width: "100%", display: "table" }}>
                        <TableHead sx={{ display: "table-header-group" }}>
                          <TableRow sx={{ display: "table-row" }}>
                            <TableCell
                              width="120"
                              align="center"
                              sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}
                            >
                              <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                Selecionar
                              </MDTypography>
                            </TableCell>
                            <TableCell
                              sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}
                            >
                              <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                Nome do Operador
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{ display: "table-row-group" }}>
                          {usuariosDestinoElegiveis.length === 0 ? (
                            <TableRow sx={{ display: "table-row" }}>
                              <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                                <MDTypography variant="button" color={darkMode ? "white" : "text"}>
                                  Nenhum operador ativo encontrado com os filtros selecionados.
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            usuariosDestinoElegiveis.map((u) => {
                              const isChecked = selectedDestinosMultiplos.includes(u.idEncrypted);
                              return (
                                <TableRow
                                  key={u.idEncrypted}
                                  hover
                                  onClick={() => handleToggleDestinoMultiplo(u.idEncrypted)}
                                  sx={{
                                    display: "table-row",
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor: darkMode ? "rgba(255,255,255,0.08) !important" : "rgba(0,0,0,0.04) !important"
                                    }
                                  }}
                                >
                                  <TableCell
                                    width="120"
                                    align="center"
                                    sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}
                                  >
                                    <Switch checked={isChecked} color="info" />
                                  </TableCell>
                                  <TableCell
                                    sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}
                                  >
                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                      {u.nome}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* PAINEL DE RESUMO DA AÇÃO */}
                <Grid item xs={12}>
                  <MDBox
                    p={2}
                    borderRadius="md"
                    sx={{
                      background: darkMode ? "#393c47" : "#e3f2fd",
                      border: "1px solid",
                      borderColor: darkMode ? "#505461" : "#90caf9"
                    }}
                  >
                    <MDBox display="flex" alignItems="center">
                      <Icon color="info" sx={{ mr: 1 }}>
                        info
                      </Icon>
                      <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "dark"}>
                        {getCalculoDestinoText()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>

                {/* BOTÃO DE EXECUÇÃO */}
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="large"
                    onClick={handleExecutarRedistribuicao}
                    disabled={loadingExecucao || !resumoOrigem || getQuantidadeDisponivel() === 0}
                    startIcon={loadingExecucao ? <CircularProgress size={18} color="inherit" /> : <Icon>swap_horiz</Icon>}
                  >
                    {loadingExecucao ? "Redistribuindo..." : "Executar Redistribuição de Carteira"}
                  </MDButton>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}

        {/* MODAL DE LISTAGEM DE CASOS NA FILA */}
        <Dialog
          open={openCasosModal}
          onClose={() => setOpenCasosModal(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: darkMode ? "#393c47" : "#ffffff",
              color: darkMode ? "#ffffff" : "#000000"
            }
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <MDTypography variant="h5" color={darkMode ? "white" : "dark"} fontWeight="bold">
              Casos na Fila ({casosFiltrados.length} / {casosList.length} fichas)
            </MDTypography>
            <IconButton onClick={() => setOpenCasosModal(false)} sx={{ color: darkMode ? "#ffffff" : "#000000" }}>
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ borderColor: darkMode ? "#505461" : "#e0e0e0" }}>
            <MDBox mb={2}>
              <MDInput
                label="Pesquisar por Devedor, CPF/CNPJ, Cliente, Carteira ou ID Ficha..."
                value={filtroBuscaCaso}
                onChange={(e) => setFiltroBuscaCaso(e.target.value)}
                fullWidth
              />
            </MDBox>

            {loadingCasos ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress color="info" />
              </MDBox>
            ) : (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader sx={{ width: "100%", display: "table" }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow sx={{ display: "table-row" }}>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          ID Ficha
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          Nome do Devedor
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          CPF/CNPJ
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          Cliente
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          Carteira
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          Status Ficha
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #e0e0e0" }}>
                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          Agendamento
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ display: "table-row-group" }}>
                    {casosFiltrados.length === 0 ? (
                      <TableRow sx={{ display: "table-row" }}>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <MDTypography variant="button" color={darkMode ? "white" : "text"}>
                            Nenhum caso encontrado para o filtro digitado.
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      casosFiltrados.map((c) => (
                        <TableRow key={c.idFicha} hover sx={{ display: "table-row" }}>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "dark"}>
                              {c.idFicha}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                              {c.nomeDevedor}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                              {c.documento || "-"}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                              {c.cliente}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                              {c.carteira}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <Chip label={c.statusFicha || "Sem Status"} size="small" color="info" />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", pt: 1.5, borderBottom: darkMode ? "1px solid #505461" : "1px solid #f0f0f0" }}>
                            <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                              {c.agendamento}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>

          <DialogActions>
            <MDButton onClick={() => setOpenCasosModal(false)} color="secondary">
              Fechar
            </MDButton>
          </DialogActions>
        </Dialog>

        <MDAuditModal
          open={openAuditModal}
          onClose={() => setOpenAuditModal(false)}
          title="Histórico de Redistribuições de Carteiras"
          loading={loadingAudit}
          logs={auditLogs}
          entityLabel="Redistribuição"
        />
      </Grid>
    </MDBox>
  );
}

export default RedistribuicaoCarteira;
